<template>
    <div>
        <v-theme-provider :theme="theme">
            <v-expansion-panels
                v-show="!hideExtraOptions"
                v-model="expanded"
                style="background-color: transparent"
                dark
                flat
                class="additional-options"
            >
                <v-expansion-panel style="background-color: transparent">
                    <slot name="header">
                        <v-expansion-panel-title
                            :class="{'text-caption': $breakpoint.smAndDown}"
                            class="pl-1 width-auto"
                        >
                            {{ $t('extra_options') }}
                        </v-expansion-panel-title>
                    </slot>
                    <v-expansion-panel-text eager>
                        <slot />
                        <v-form>
                            <v-row :no-gutters="isMobile" class="additional-options__form-row align-center">
                                <v-col v-if="enabled('rating')" cols="12" md="2">
                                    <select-option
                                        ref="category"
                                        name="category"
                                        :items="ratingItems"
                                        :label="$t('filterTitles.category')"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        autocomplete-disabled
                                    />
                                </v-col>
                                <v-col v-if="enabled('meal')" cols="12" md="4" lg="3" xl="2">
                                    <select-option
                                        ref="mealType"
                                        name="mealType"
                                        :items="mealTypeItems"
                                        :label="$t('filterTitles.mealTypes')"
                                        multiple
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        :clearable="false"
                                        autocomplete-disabled
                                    />
                                </v-col>
                                <v-col v-if="enabled('flight-class')" cols="12" md="3">
                                    <select-option
                                        ref="flightClass"
                                        name="flightClass"
                                        :items="flightClasses"
                                        :label="$t('flights.options.class')"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        :default-value="flightClassDefaultValue"
                                    />
                                </v-col>
                                <v-col v-if="enabled('visaEntryType')" cols="12" md="3">
                                    <select-option
                                        ref="visaEntryType"
                                        name="entryType"
                                        :items="visaEntryType"
                                        :label="$t('visa.entry_type')"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        :default-value="flightClassDefaultValue"
                                    />
                                </v-col>
                                <v-col v-if="enabled('airlines')" cols="12" md="3">
                                    <select-option
                                        ref="airlineCodes"
                                        name="airlineCodes"
                                        :multiple="true"
                                        :items="airlines"
                                        :label="$t('flights.options.airlines')"
                                        small-cheaps
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                    />
                                </v-col>
                                <template v-if="enabled('flight-options')">
                                    <v-col cols="12" md="2">
                                        <checkbox-option
                                            ref="baggageOnly"
                                            name="baggageOnly"
                                            :label="$t('flights.options.baggage_only')"
                                            :default-value="false"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="2" :class="$i18n.locale === 'ru' ? 'ref-up-on-small' : ''">
                                        <checkbox-option
                                            ref="refundableOnly"
                                            name="refundableOnly"
                                            :label="$t('flights.options.refundable_only')"
                                            :default-value="false"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <checkbox-option
                                            ref="option"
                                            name="directFlight"
                                            :label="$t('flights.options.direct_only')"
                                            :default-value="false"
                                        />
                                    </v-col>
                                </template>
                                <template v-if="enabled('transfer-type')">
                                    <v-col cols="12" md="2">
                                        <checkbox-option
                                            ref="privateTransfer"
                                            name="privateTransfer"
                                            :label="$t('transfers.private_transfer')"
                                            :default-value="true"
                                        />
                                        <checkbox-option
                                            ref="groupTransfer"
                                            name="groupTransfer"
                                            :label="$t('transfers.shared_transfer')"
                                            :default-value="true"
                                        />
                                    </v-col>
                                </template>
                                <template v-if="isTO1">
                                    <slot name="customOptions" />
                                    <v-col v-if="enabled('duration') && supplierItems.length" cols="12" md="2">
                                        <number-option
                                            ref="duration"
                                            name="duration"
                                            :label="$t('duration_length')"
                                            :min="1"
                                            :max="99"
                                            hide-details
                                        />
                                    </v-col>
                                </template>
                                <template v-if="enabled('activity-type')">
                                    <v-col cols="12" md="2">
                                        <checkbox-option
                                            ref="privateActivity"
                                            name="privateActivity"
                                            :label="$t('activities.private_activity')"
                                            :default-value="true"
                                        />
                                        <checkbox-option
                                            ref="sharedActivity"
                                            name="sharedActivity"
                                            :label="$t('activities.shared_activity')"
                                            :default-value="true"
                                        />
                                    </v-col>
                                </template>
                                <v-col v-if="enabled('passengers')" cols="12" md="2">
                                    <number-option
                                        ref="passengers"
                                        name="passengers"
                                        :label="$t('carsRent.number_of_passengers')"
                                        hide-details
                                    />
                                </v-col>
                                <v-col v-if="enabled('availableOnly')" cols="12" md="2">
                                    <checkbox-option
                                        ref="availableOnly"
                                        name="availableOnly"
                                        :label="$t('carsRent.availableOnly')"
                                        :default-value="false"
                                    />
                                </v-col>
                                <v-col v-if="enabled('freeOnly')" cols="12" md="2">
                                    <checkbox-option
                                        ref="freeOnly"
                                        name="freeOnly"
                                        :label="$t('free_only')"
                                        :default-value="false"
                                    />
                                </v-col>
                                <v-col v-if="enabled('adultsOnly')" cols="12" md="2">
                                    <checkbox-option
                                        ref="services"
                                        name="services"
                                        :value-name="'ADULTS_ONLY'"
                                        :label="$t('serviceNames.adults_only')"
                                    />
                                </v-col>
                            </v-row>
                            <v-divider v-if="isMobile" class="my-3" />
                            <v-row :no-gutters="isMobile" class="align-center mt-0">
                                <v-col
                                    v-if="isTO1 && (enabled('internalSuppliers') || enabled('externalSuppliers'))"
                                    cols="12"
                                    md="2 "
                                    :style="{opacity: disabledInternalSupplier ? '0.5' : '1'}"
                                >
                                    <template v-if="enabled('internalSuppliers')">
                                        <checkbox-option
                                            ref="internalSuppliers"
                                            name="internalSuppliers"
                                            :default-value="true"
                                            can-be-false
                                            :disabled="disabledInternalSupplier"
                                            :label="$t('internal_suppliers')"
                                        />
                                    </template>
                                    <template v-if="enabled('externalSuppliers')">
                                        <checkbox-option
                                            ref="externalSuppliers"
                                            name="externalSuppliers"
                                            :default-value="true"
                                            can-be-false
                                            :label="$t('external_suppliers')"
                                            :disabled="disabledExternalSupplier"
                                        />
                                    </template>
                                </v-col>
                                <v-col v-if="isTO1 && enabled('supplierId') && supplierItems.length" cols="12" md="2">
                                    <select-option
                                        ref="supplierId"
                                        name="supplierId"
                                        :items="supplierItems"
                                        :label="$t('supplier_id')"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        @change="onSupplierItemChange"
                                    />
                                </v-col>
                                <v-col v-if="enabled('citizenship') && !isCitizenshipMainOption" cols="12" md="2">
                                    <select-option
                                        ref="citizenshipId"
                                        :key="`citizenshipId-${defaultCitizenshipId}`"
                                        name="citizenshipId"
                                        :items="citizenshipItems"
                                        :label="$t('citizenship')"
                                        :default-value="defaultCitizenshipId"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                        @change="changeCitizenship"
                                    />
                                </v-col>
                                <v-col v-if="enabled('driverCitizenship')" cols="12" md="2">
                                    <select-option
                                        ref="driverCitizenshipId"
                                        :key="`driverCitizenshipId-${defaultCitizenshipId}`"
                                        name="driverCitizenshipId"
                                        :items="citizenshipItems"
                                        :label="$t('citizenship')"
                                        :default-value="defaultCitizenshipId"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                    />
                                </v-col>
                                <v-col v-if="enabled('applicantCountryId')" cols="12" md="2">
                                    <select-option
                                        ref="applicantCountryId"
                                        :key="`applicantCountryId-${defaultCitizenshipId}`"
                                        name="applicantCountryId"
                                        :items="citizenshipItems"
                                        :label="$t('citizenship')"
                                        :default-value="defaultCitizenshipId"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                    />
                                </v-col>
                                <v-col v-if="enabled('promocode') && searchFormOptions.promoCode" cols="12" md="2">
                                    <text-option
                                        ref="promoCode"
                                        name="promoCode"
                                        :label="$t('promocode')"
                                        :single-line="singleLine"
                                        :outlined="outlined"
                                    />
                                </v-col>
                                <v-col
                                    v-if="searchFormOptions.showCommission && (isAgency || isTourOperator)"
                                    cols="12"
                                    md="2"
                                >
                                    <checkbox-option
                                        ref="showCommission"
                                        name="showCommission"
                                        :label="$t('show_commission')"
                                        :default-value="showCommissionDefaultValue"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-theme-provider>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Setup, Vue, Watch} from 'vue-facing-decorator'
    import TextOption from '@/components/snippets/AdditionalSearchOptions/TextOption.vue'
    import SelectOption from '@/components/snippets/AdditionalSearchOptions/SelectOption.vue'
    import CheckboxOption from '@/components/snippets/AdditionalSearchOptions/CheckboxOption.vue'
    import NumberOption from '@/components/snippets/AdditionalSearchOptions/NumberOption.vue'

    //TODO Need to remake this shit
    @Component({
        components: {
            CheckboxOption,
            SelectOption,
            TextOption,
            NumberOption,
        },
    })
    export class AdditionalOptionsWrapper extends Vue {
        @Prop({default: false, type: Boolean}) outlined
        @Prop({default: false}) singleLine
        @Prop({default: true}) showIcon
        @Prop({default: () => []}) options
        @Prop({default: () => []}) suppliers
        @Prop({default: false, type: Boolean}) isCitizenshipMainOption
        @Setup(() => useCustomizationHelpers().additionalOptionsExpanded) expanded

        get theme() {
            return this.$conf.public.layout.searchForm.light ? 'light' : 'dark'
        }

        promocode = null
        ratingItems = []
        mealTypeItems = []

        disabledInternalSupplier = false
        disabledExternalSupplier = false
        hideSupplierItems = false

        @Watch('locale')
        onLocaleChange() {
            this.setMealTypes()
            this.setRatings()
        }

        async mounted() {
            await this.$store.restored
            this.setMealTypes()
            this.setRatings()
            if (this.enabled('airlines')) {
                this.loadAirlines()
            }
        }

        @Emit()
        changeCitizenship(e) {
            return e.value
        }

        enabled(val) {
            return this.options.indexOf(val) >= 0
        }

        loadAirlines() {
            flightsStore.loadAirlines()
        }

        get showCommissionDefaultValue() {
            return true
        }

        get visaEntryType() {
            return [
                {
                    name: this.$t('visa_entry_type.SINGLE'),
                    value: 'SINGLE',
                },
                {
                    name: this.$t('visa_entry_type.DOUBLE'),
                    value: 'DOUBLE',
                },
                {
                    name: this.$t('visa_entry_type.MULTIPLE'),
                    value: 'MULTIPLE',
                },
            ]
        }

        get flightClasses() {
            return [
                {
                    name: this.$t('flights.classes.ECONOMY'),
                    value: 'ECONOMY',
                },
                {
                    name: this.$t('flights.classes.PREMIUM_ECONOMY'),
                    value: 'PREMIUM_ECONOMY',
                },
                {
                    name: this.$t('flights.classes.FIRST'),
                    value: 'FIRST',
                },
                {
                    name: this.$t('flights.classes.BUSINESS'),
                    value: 'BUSINESS',
                },
            ]
        }

        setMealTypes() {
            this.mealTypeItems = [
                {
                    name: this.$t('filters_mealTypes.RO'),
                    value: 'RO',
                },
                {
                    name: this.$t('filters_mealTypes.BB'),
                    value: 'BB',
                },
                {
                    name: this.$t('filters_mealTypes.HB'),
                    value: 'HB',
                },
                {
                    name: this.$t('filters_mealTypes.FB'),
                    value: 'FB',
                },
                {
                    name: this.$t('filters_mealTypes.BBT'),
                    value: 'BBT',
                },
                {
                    name: this.$t('filters_mealTypes.HBT'),
                    value: 'HBT',
                },
                {
                    name: this.$t('filters_mealTypes.FBT'),
                    value: 'FBT',
                },
                {
                    name: this.$t('filters_mealTypes.ALL'),
                    value: 'ALL',
                },
            ]
        }

        setRatings() {
            this.ratingItems = Array.from(Array(5).keys())
                .map(key => {
                    return {
                        name: this.$t('filters_category', key + 1) + ' ' + this.$t('and_more'),
                        value: (key + 1).toString(),
                    }
                })
                .sort((a, b) => {
                    return b.value - a.value
                })
        }

        setCitizenshipId(value) {
            this.citizenshipId = value
        }

        onSupplierItemChange(e) {
            if (e.value) {
                this.disabledInternalSupplier = true
                this.disabledExternalSupplier = true

                if (this.$refs?.externalSuppliers) {
                    this.$refs.externalSuppliers.value = true
                }
                if (this.$refs?.internalSuppliers) {
                    this.$refs.internalSuppliers.value = true
                }
            } else {
                this.disabledInternalSupplier = false
                this.disabledExternalSupplier = false
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get flightClassDefaultValue() {
            return this.flightClasses.find(e => e.value === this.searchFormOptions.flightClassDefault)?.value
        }

        get citizenshipItems() {
            return runtimeStore.restrictedCountries.map(({id, name}) => {
                return {
                    name,
                    value: id,
                }
            })
        }

        get locale() {
            return this.$i18n.locale
        }

        get defaultCitizenshipId() {
            return authStore.defaultCitizenshipId
        }

        get searchFormOptions() {
            return this.$conf.public.searchFormOptions
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get supplierItems() {
            return this.suppliers
                .map(el => ({
                    name: el.name,
                    value: el.id,
                }))
                ?.sort((a, b) => (a.name > b.name ? 1 : -1))
        }

        get airlines() {
            return flightsStore.airlines.map(({codes, name}) => {
                return {
                    name: name[this.$i18n.locale] || name['en'],
                    value: codes.IATA,
                }
            })
        }

        get hideExtraOptions() {
            return false
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default AdditionalOptionsWrapper
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    :deep(.v-expansion-panel-text__wrapper) {
        padding-left: 0;
        padding-right: 0;
    }

    @media (min-width: 1350px) and (max-width: 1450px) {
        .ref-up-on-small {
            margin-top: -8px;
        }
    }

    .additional-options form {
        margin-top: -20px;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        :deep(div) {
            input,
            .v-label {
                font-size: 12px !important;
            }
        }
    }
</style>
